import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Truck, ShieldCheck, Power, PowerOff, RefreshCw, Plus,
  CheckCircle2, AlertTriangle, Copy, Search, Loader2, KeyRound
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const badge = (active) => active
  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
  : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

export default function ShipStationAccountsPage() {
  const { token } = useAuth();
  const [whoami, setWhoami] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [banner, setBanner] = useState(null);
  const [q, setQ] = useState('');
  const [showProvision, setShowProvision] = useState(false);
  const [provisionUserId, setProvisionUserId] = useState('');
  const [provisionForce, setProvisionForce] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const call = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders, ...(opts.headers || {}) } });
    const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
    return { ok: res.ok, status: res.status, data };
  }, [token]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const w = await call('/api/shipstation/partners/whoami');
    setWhoami(w.data);
    const a = await call('/api/shipstation/partners/accounts?page=1&page_size=100');
    if (a.ok) { setAccounts(a.data.records); setTotal(a.data.total); }
    setLoading(false);
  }, [call]);

  useEffect(() => { refresh(); }, [refresh]);

  const doAction = async (kind, accountId) => {
    setBusy(`${kind}-${accountId}`); setBanner(null);
    const method = kind === 'rotate' ? 'POST' : 'PUT';
    const path = kind === 'rotate' ? `/api/shipstation/partners/accounts/${accountId}/rotate-key`
      : `/api/shipstation/partners/accounts/${accountId}/${kind}`;
    const res = await call(path, { method });
    if (res.ok) {
      setBanner({ type: 'ok', text: `Account ${accountId}: ${kind} succeeded (${res.data.mode})` });
      await refresh();
    } else {
      setBanner({ type: 'err', text: res.data?.detail || `${kind} failed` });
    }
    setBusy('');
  };

  const doProvision = async () => {
    if (!provisionUserId.trim()) return;
    setBusy('provision'); setBanner(null);
    const res = await call('/api/shipstation/partners/provision', {
      method: 'POST',
      body: JSON.stringify({ user_id: provisionUserId.trim(), force: provisionForce }),
    });
    if (res.ok) {
      setBanner({ type: 'ok', text: `Provisioned account ${res.data.account_id} for merchant ${provisionUserId} (${res.data.mode})` });
      setProvisionUserId(''); setProvisionForce(false); setShowProvision(false);
      await refresh();
    } else {
      setBanner({ type: 'err', text: res.data?.detail || 'Provision failed' });
    }
    setBusy('');
  };

  const filtered = accounts.filter(a => {
    if (!q) return true;
    const s = q.toLowerCase();
    return String(a.account_id).includes(s)
      || (a.merchant_email || '').toLowerCase().includes(s)
      || (a.merchant_company || '').toLowerCase().includes(s)
      || (a.company_name || '').toLowerCase().includes(s)
      || (a.user_id || '').toLowerCase().includes(s);
  });

  const modeChip = whoami?.mode === 'live'
    ? <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">LIVE · partner {whoami.partner_id}</span>
    : <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">DRY-RUN · no partner key</span>;

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6" data-testid="ss-accounts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">ShipStation Accounts</h1>
              <p className="text-sm text-slate-500">Partner-provisioned ShipStation API sub-accounts, one per verified merchant.</p>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-3">
            {whoami && modeChip}
            <span className="text-sm text-slate-500">{total} account{total !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refresh} data-testid="ss-refresh"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          {/* "Test full flow" se saco con la revision del 26-oct (A.11): compraba y anulaba
              una etiqueta real con el primer comerciante aprobado. */}
          <Button onClick={() => setShowProvision(true)} data-testid="ss-provision-open"><Plus className="w-4 h-4 mr-2" />Provision account</Button>
        </div>
      </div>

      {whoami?.mode === 'dry_run' && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200 flex gap-3" data-testid="ss-dryrun-banner">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <b>Dry-run mode.</b> No ShipStation Partner API key is stored. Every provision, enable, disable and key rotation runs against realistic mock responses.
            To go live, add a secret with provider <code className="px-1 rounded bg-white/60 dark:bg-black/40">shipstation_partners</code> at
            {' '}<a className="underline font-semibold" href="/dashboard/secrets" data-testid="ss-secrets-link">/dashboard/secrets</a>.
          </div>
        </div>
      )}

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300'}`} data-testid="ss-banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{banner.text}</span>
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search account ID, email, or company"
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
          data-testid="ss-search" />
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Account</th>
              <th className="px-4 py-3 text-left">Merchant</th>
              <th className="px-4 py-3 text-left">Provisioned</th>
              <th className="px-4 py-3 text-left">Mode</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">API key</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="ss-account-rows">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No accounts yet. Provision one for a verified merchant.</td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ss-row-${r.account_id}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.account_id}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.company_name || r.merchant_company || '—'}</div>
                  <div className="text-xs text-slate-500">{r.merchant_email || r.user_id}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.provisioned_at).toLocaleString()}<div className="text-[10px]">by {r.provisioned_by === 'auto' ? 'auto (KYS approved)' : 'admin'}</div></td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${r.mode === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{(r.mode || 'dry_run').toUpperCase()}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge(r.active)}`}>{r.active ? 'ACTIVE' : 'DISABLED'}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-3 h-3" />
                    …{r.api_key_last4 || '????'}
                    <button onClick={() => navigator.clipboard.writeText(`account_id:${r.account_id}`)} className="text-slate-400 hover:text-emerald-500" title="Copy account id" data-testid={`ss-copy-${r.account_id}`}>
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {r.active ? (
                      <Button size="sm" variant="outline" onClick={() => doAction('disable', r.account_id)} disabled={busy === `disable-${r.account_id}`} data-testid={`ss-disable-${r.account_id}`}>
                        <PowerOff className="w-3 h-3 mr-1" /> Disable
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => doAction('enable', r.account_id)} disabled={busy === `enable-${r.account_id}`} data-testid={`ss-enable-${r.account_id}`}>
                        <Power className="w-3 h-3 mr-1" /> Enable
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => doAction('rotate', r.account_id)} disabled={busy === `rotate-${r.account_id}`} data-testid={`ss-rotate-${r.account_id}`}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Rotate key
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showProvision && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowProvision(false)} data-testid="ss-provision-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h3 className="text-lg font-semibold">Provision ShipStation account</h3>
                <p className="text-xs text-slate-500">The merchant must have completed KYS with status <b>approved</b> or <b>pending_review</b>.</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Merchant user ID</label>
              <input value={provisionUserId} onChange={e => setProvisionUserId(e.target.value)} placeholder="uuid…" className="w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm font-mono" data-testid="ss-provision-user" />
            </div>
            <label className="flex items-center gap-2 text-sm" data-testid="ss-provision-force">
              <input type="checkbox" checked={provisionForce} onChange={e => setProvisionForce(e.target.checked)} />
              Force provision for <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">pending_review</code> merchants (compliance override)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowProvision(false)}>Cancel</Button>
              <Button onClick={doProvision} disabled={busy === 'provision' || !provisionUserId.trim()} data-testid="ss-provision-submit">
                {busy === 'provision' ? 'Provisioning…' : 'Provision'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
